/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { LOCAL_STORAGE } from "../../globals/globals";
import { IActionProps, IDeviceAction } from "../../store/api/devices/types";
import { IAddScheduledTask } from "../../store/api/tasks/types";
import { Action, TASK } from "./types";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { IKeyValueArray } from "../Administrations/types";
import { IActionOptions } from "../../store/api/deviceConfig/types";
import { DasModelNames } from "../../globals/constants";

export const actionList: { [key: string]: Action } = {
  RebootServicesNode: {
    title: "Reboot Device",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.REBOOT.NAME,
    taskName: TASK.REBOOT.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Reboot this device?",
  },
  Delete: {
    title: "Delete Device",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: "Delete",
    actionName: "Delete",
    deviceKey: "",
    dialogue: "Deleting this device will also remove all history data including alarms, events and KPIs. Are you sure you want to delete?",
  },
  NRCellEnable: {
    title: "Enable Cell",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.NRCELLENABLE.NAME,
    taskName: TASK.NRCELLENABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Enable Cell?",
  },
  NRCellDisable: {
    title: "Disable Cell",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.NRCELLDISABLE.NAME,
    taskName: TASK.NRCELLDISABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Disable Cell?",
  },
  LTECellEnable: {
    title: "Enable Cell",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.LTECELLENABLE.NAME,
    taskName: TASK.LTECELLENABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Enable Cell?",
  },
  LTECellDisable: {
    title: "Disable Cell",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.LTECELLDISABLE.NAME,
    taskName: TASK.LTECELLDISABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Disable Cell?",
  },
  RefreshFullDevice: {
    title: "Refresh",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: "Refresh",
    actionName: "RefreshFullDevice",
    deviceKey: "",
    dialogue: "Refresh will delete all the Pending Configurations, are you sure you want to continue?",
  },
  DasDiscoveryRefresh: {
    title: "Refresh Discovery",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: "Refresh",
    actionName: "DasDiscoveryRefresh",
    deviceKey: "",
    dialogue: "Are you sure you want to trigger discovery for this device?",
  },
  RadioNodeEnable: {
    title: "Enable RadioNode",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: TASK.RADIONODEENABLE.NAME,
    actionName: TASK.RADIONODEENABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Enable RadioNode?",
  },
  RadioNodeDisable: {
    title: "Disable RadioNode",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: TASK.RADIONODEDISABLE.NAME,
    actionName: TASK.RADIONODEDISABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Disable RadioNode?",
  },
  PartialRefresh: {
    title: "Refresh",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: "Refresh",
    actionName: "PartialRefresh",
    deviceKey: "",
    dialogue: "Refresh will delete all the Pending Configurations, are you sure you want to continue?",
  },
  RebootRadioNode: {
    title: "Reboot RadioNode",
    component: "",
    isTask: true,
    isComponentRequired: false,
    name: TASK.REBOOTRADIONODE.NAME,
    taskName: TASK.REBOOTRADIONODE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Reboot RadioNode?",
  },
  DuEnable: {
    title: "Enable Du",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: TASK.DUENABLE.NAME,
    actionName: TASK.DUENABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Enable Du?",
  },
  DuDisable: {
    title: "Disable Du",
    component: "",
    isTask: false,
    isComponentRequired: false,
    name: TASK.DUDISABLE.NAME,
    actionName: TASK.DUDISABLE.VALUE,
    deviceKey: "",
    dialogue: "Are you sure you want to Disable Du?",
  },
};

export const taskActionCommonPayload = (taskFormData: IDeviceAction) => {
  return {
    taskName: taskFormData.taskName,
    deviceKey: taskFormData.deviceKey,
    taskProperties: {},
  };
};

export const taskActionPayload = {
  commonPayload: (taskFormData: any) => {
    return taskActionCommonPayload(taskFormData);
  },

  RebootServicesNode: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.REBOOT.VALUE,
      taskProperties: {},
    };
  },
  LTEREMStart: (remStartData: any) => {
    return {
      taskName: remStartData.taskName,
      deviceKey: remStartData.deviceKey,
      taskProperties: {
        zones: remStartData?.zones || "",
        [`use-ref-sig-pwr-cfg`]: remStartData.referenceSinglePowerEnabled || "false",
      },
    };
  },
  LTEREMStop: (remStartData: any) => {
    return {
      taskName: remStartData.taskName,
      deviceKey: remStartData.deviceKey,
      taskProperties: {},
    };
  },
  NRCellEnable: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.NRCELLENABLE.VALUE,
      taskProperties: {
        cellId: "" + `${taskFormData.selectedData.id}` + "",
      },
    };
  },
  NRCellDisable: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.NRCELLDISABLE.VALUE,
      taskProperties: {
        cellId: "" + `${taskFormData.selectedData.id}` + "",
      },
    };
  },
  LTECellEnable: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.LTECELLENABLE.VALUE,
      taskProperties: {
        cellId: "" + `${taskFormData.selectedData.id}` + "",
      },
    };
  },
  LTECellDisable: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.LTECELLDISABLE.VALUE,
      taskProperties: {
        cellId: "" + `${taskFormData.selectedData.id}` + "",
      },
    };
  },
  SoftwareDownloadUpdate: (taskFormData: IAddScheduledTask) => {
    // const taskPayload = taskActionCommonPayload(taskFormData);
    const fileArray = !!taskFormData?.fileName ? taskFormData?.fileName.split("/") : [];
    const localFileName = fileArray.length > 0 ? fileArray[fileArray.length - 1] : taskFormData?.fileName;
    return {
      deviceKey: taskFormData.deviceName,
      taskName: taskFormData.taskName?.value,
      taskProperties: {
        RN_ID: taskFormData.deviceName || "",
        PROTOCOL: taskFormData?.protocol || "",
        VALUE: taskFormData?.softwareUpdateClean || "",
        SSH_KEY_INDEX: taskFormData?.sshKeyIndex || "",
        FILE_TRANSFER_TYPE: taskFormData?.serverType || "",
        SSH_OPERATION: taskFormData?.sshOperation,
        PORT: taskFormData?.port || 22,
        USERNAME: taskFormData?.username || "",
        PASSWORD: taskFormData?.password || "",
        HOSTNAME: taskFormData?.hostName || "",
        LOCAL_FILENAME: localFileName || "",
        REMOTE_FILENAME: taskFormData.fileName || "",
      },
    };
  },
  RebootRadioNode: (taskFormData) => {
    const taskPayload = taskActionCommonPayload(taskFormData);
    return {
      ...taskPayload,
      taskName: TASK.REBOOTRADIONODE.VALUE,
      taskProperties: {
        node: "" + `${taskFormData.selectedData.id}` + "",
      },
    };
  },
} as { [key: string]: (arg: any) => any };

export const actionCommonPayload = (actionFormData: IDeviceAction) => {
  return {
    deviceKey: actionFormData.deviceKey,
    actionName: actionFormData.actionName,
  };
};

export const actionPayload = {
  commonPayload: (actionFormData: any) => {
    return actionCommonPayload(actionFormData);
  },

  Delete: (actionFormData) => {
    return {
      deviceKey: actionFormData.deviceKey,
    };
  },
  RefreshFullDevice: (actionFormData) => {
    return {
      deviceKey: actionFormData.deviceKey,
    };
  },
  DasDiscoveryRefresh: (actionFormData) => {
    return {
      deviceKey: actionFormData.deviceKey,
    };
  },
  RadioNodeEnable: (actionFormData) => {
    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const field = "Enable";
    let oldValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "false" };
    let newValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "true" };
    return {
      operationType: "UPDATE",
      userId: localStorage.getItem(LOCAL_STORAGE.USER),
      rowkey: "",
      tableId: "",
      deviceObjectKey: actionFormData.deviceKey,
      oldValue: { ...oldValue },
      newValue: { ...newValue },
    };
  },
  RadioNodeDisable: (actionFormData) => {
    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const field = "Enable";
    let oldValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "true" };
    let newValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "false" };
    return {
      operationType: "UPDATE",
      userId: localStorage.getItem(LOCAL_STORAGE.USER),
      rowkey: "",
      tableId: "",
      deviceObjectKey: actionFormData.deviceKey,
      oldValue: { ...oldValue },
      newValue: { ...newValue },
    };
  },
  PartialRefresh: (actionFormData) => {
    const radioNodePath = "InternetGatewayDevice.RadioNode";
    let partialRefreshPath = [`${radioNodePath}.${actionFormData.selectedData.id}.`];
    return {
      deviceKey: actionFormData.deviceKey,
      dataPathList: partialRefreshPath,
    };
  },
  DuEnable: (actionFormData) => {
    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const field = "Enable";
    let oldValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "false" };
    let newValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "true" };
    return {
      operationType: "UPDATE",
      userId: localStorage.getItem(LOCAL_STORAGE.USER),
      rowkey: "",
      tableId: "",
      deviceObjectKey: actionFormData.deviceKey,
      oldValue: { ...oldValue },
      newValue: { ...newValue },
    };
  },
  DuDisable: (actionFormData) => {
    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const field = "Enable";
    let oldValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "true" };
    let newValue = { [`${radioNodePath}.${actionFormData.selectedData.id}.${field}`]: "false" };
    return {
      operationType: "UPDATE",
      userId: localStorage.getItem(LOCAL_STORAGE.USER),
      rowkey: "",
      tableId: "",
      deviceObjectKey: actionFormData.deviceKey,
      oldValue: { ...oldValue },
      newValue: { ...newValue },
    };
  },
} as { [key: string]: (arg: any) => any };

export const optionsFilterProps = (deviceKey: string, data: any, actionOptions: IActionOptions[], inventoryDiscoveryEnabled: boolean = true) : IActionOptions[] => {
  let options: IActionOptions[] = [];
  if(data?.data === undefined) {
    const isDas = DasModelNames.some((m: any) => m.value === data?.modelName);
    if(data?.configurationStatus?.toLowerCase() === "offline" || isDas) {
      
      options = actionOptions.filter(el =>
        el?.value?.toLowerCase() === "delete" ||
        (isDas && el?.value === "DasDiscoveryRefresh" && inventoryDiscoveryEnabled)
      );
    }
    else return actionOptions.filter(el => el?.value !== "DasDiscoveryRefresh");
  }
  else {
    let selectedRow = data?.data?.find((item: any) => item?.objectKey === deviceKey)
	const isDas = DasModelNames.some((m: any) => m.value === selectedRow?.modelName);
    if(selectedRow?.configurationStatus?.toLowerCase() === "offline" || isDas) {
      
      options = actionOptions.filter(el =>
        el?.value?.toLowerCase() === "delete" ||
        (isDas && el?.value === "DasDiscoveryRefresh" && inventoryDiscoveryEnabled)
      );
    }
    else return actionOptions.filter(el => el?.value !== "DasDiscoveryRefresh");
  }
  return options || []
}

export const prepareActionProps = (
  deviceKey: string,
  name: string,
  data: any,
  actionOptions: { text: string; value: string; permissionRequired: string }[],
  inventoryLocation: string,
  ActionBtnProp: boolean,
  permissions: ProfilePermissions | null,
  inventoryDiscoveryEnabled: boolean = true
) => {
  let filteredActions: { text: string; value: string; permissionRequired: string }[] = [];

  if (permissions) {
    filteredActions = optionsFilterProps(deviceKey, data, actionOptions, inventoryDiscoveryEnabled)?.filter((a) => a.permissionRequired == null || permissions?.hasOpCodeGroup(a.permissionRequired));
  } else {
    filteredActions = optionsFilterProps(deviceKey, data, actionOptions, inventoryDiscoveryEnabled);
  }

  // if (permissions) {
  //   filteredActions = actionOptions.filter((a) => a.permissionRequired == null || permissions?.hasOpCodeGroup(a.permissionRequired));
  // } else {
  //   filteredActions = actionOptions;
  // }

  const actionProps: IActionProps = {
    deviceKey: deviceKey,
    name: name,
    selectedData: data,
    actionList: filteredActions,
    inventoryLocation: inventoryLocation,
    ActionBtnColor: ActionBtnProp,
  };
  return actionProps;
};

export const rowSelectionKey = (tableId: string) => {
  let newObj: IKeyValueArray = {};
  if (tableId === "radioNode") {
    newObj = { rnRowIds: [] };
  }
  else if(tableId === "lteCell") {
    newObj = { lteCellRowIds: [] };
  }
  else if(tableId === "nrCell") {
    newObj = { nrCellRowIds: [] };
  }
  else if(tableId === "rfFeed") {
    newObj = { rfFeedRowIds: [] };
  }
  else if(tableId === "deviceRow") {
    newObj = { deviceRowIds: [] };
  }
  return newObj;
}
